import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    Alert,
    Autocomplete,
    Badge,
    Button,
    Checkbox,
    ComboboxItem,
    Group,
    Modal,
    Paper,
    Stack,
    Table,
    Text,
    ThemeIcon,
    rgba,
    useMantineTheme,
} from "@mantine/core";
import {IconShirt} from "@tabler/icons-react";
import QrScanner from "qr-scanner";
import {toNumber} from "lodash";
import {PageLayout} from '../shell/PageLayout';
import {BooleanStatusIcon, CheckOutAction, PaidStatusIcon} from '../common/TableActionIcons';
import './CheckIn.css';
import {useDebouncedValue} from "@mantine/hooks";
import {notifications} from '@mantine/notifications';
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {DataTable, DataTableSortStatus} from "mantine-datatable";
import sortBy from "lodash/sortBy";
import {formatAdminDate} from "../../utils/formatDate";
import {filterCheckInOverviewRegistrations} from "../../utils/checkInOverviewFilter";
import {buildStaffEmailSet, isStaffRegistration} from "../../utils/staffRegistrationFilter";
import {useDataTablePagination} from '../../utils/useDataTablePagination';
import {Registration, RegistrationService, StaffService} from "../../client";
import type {app__schema__registration__Get} from "../../client";
import type {StaffMember} from '../staff/Staff';

type CheckInModalState = {
    fullName: string;
    age: number;
    paid: string;
    tshirt: string;
    requiresParentalConsent: boolean;
    alreadyCheckedIn: boolean;
    isUnpaid: boolean;
    isStaff: boolean;
};

const emptyCheckInModalState: CheckInModalState = {
    fullName: '',
    age: 0,
    paid: '',
    tshirt: '',
    requiresParentalConsent: false,
    alreadyCheckedIn: false,
    isUnpaid: false,
    isStaff: false,
};

const buildCheckInModalState = (
    registration: app__schema__registration__Get,
    staffEmails: Set<string>,
): CheckInModalState => {
    const isStaff = isStaffRegistration(registration, staffEmails);
    return {
        fullName: `${registration.firstname} ${registration.lastname}`.trim(),
        age: registration.age,
        paid: registration.paid,
        tshirt: registration.tshirt?.toUpperCase() || '—',
        requiresParentalConsent: registration.age < 18,
        alreadyCheckedIn: registration.checked_in ?? false,
        isStaff,
        isUnpaid: !isStaff && registration.paid !== 'paid',
    };
};

const confirmCheckInLabel = (isUnpaid: boolean) =>
    isUnpaid ? 'Check-in & mark as paid' : 'Confirm Check-in';

const useStaffEmails = () => {
    const {data: staffData} = useQuery({
        queryKey: ['staff'],
        queryFn: async () => {
            const response = await StaffService.getAllStaffApiV1StaffGet();
            return response as { staff_members: StaffMember[] };
        },
    });

    return useMemo(
        () => buildStaffEmailSet(staffData?.staff_members),
        [staffData],
    );
};

const CheckInOverview = () => {
    const theme = useMantineTheme();
    const queryClient = useQueryClient();
    const staffEmails = useStaffEmails();
    const {isLoading, data} = useQuery({
        queryKey: ['registrations'],
        queryFn: RegistrationService.getAllRegistrationsApiV1RegistrationsGet,
    });

    const paidRegistrations = useMemo(
        () => (data ?? []).filter((registration) => registration.paid === 'paid'),
        [data],
    );

    const checkedInCount = useMemo(
        () => (data ?? []).filter((registration) => registration.checked_in).length,
        [data],
    );

    const remainingCount = paidRegistrations.filter((registration) => !registration.checked_in).length;

    const [hideCheckedIn, setHideCheckedIn] = useState(false);
    const [hideUnpaid, setHideUnpaid] = useState(true);

    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Registration>>({
        columnAccessor: 'lastname',
        direction: 'asc',
    });

    const visibleRegistrations = useMemo(
        () => filterCheckInOverviewRegistrations(
            data ?? [],
            {hideCheckedIn, hideUnpaid},
            staffEmails,
        ),
        [data, hideCheckedIn, hideUnpaid, staffEmails],
    );

    const sortedRecords = useMemo(() => {
        const sorted = sortBy(visibleRegistrations, sortStatus.columnAccessor) as Registration[];
        const mapped = sorted.map((registration) => ({
            ...registration,
            name: `${registration.lastname}, ${registration.firstname}`,
        }));
        return sortStatus.direction === 'desc' ? mapped.reverse() : mapped;
    }, [visibleRegistrations, sortStatus]);

    const {paginate, getPaginationProps} = useDataTablePagination();
    const records = useMemo(
        () => paginate(sortedRecords),
        [sortedRecords, paginate],
    );

    const handleCheckOut = (registration: Registration) => {
        RegistrationService.checkOutApiV1RegistrationCheckInRegistrationIdDelete({
            registrationId: registration.id,
        })
            .then(() => {
                queryClient.invalidateQueries({queryKey: ['registrations']});
            })
            .catch((error: { body?: { detail?: string }; message?: string }) => {
                notifications.show({
                    color: 'red',
                    title: 'Check-out failed',
                    message: error.body?.detail ?? error.message ?? 'Request failed',
                });
            });
    };

    return (
        <Stack mt="xl" gap="md">
            <Text size="lg">
                {remainingCount} check-in{remainingCount === 1 ? '' : 's'} still expected
                {' '}({checkedInCount} checked in)
            </Text>
            <DataTable
                withTableBorder
                striped
                highlightOnHover
                records={records}
                fetching={isLoading}
                loaderType="oval"
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                {...getPaginationProps(sortedRecords.length)}
                columns={[
                    {
                        accessor: 'id',
                        title: 'ID',
                        sortable: true,
                    },
                    {
                        accessor: 'name',
                        title: 'Name',
                        sortable: true,
                    },
                    {
                        accessor: 'checked_in',
                        title: 'Checked-In',
                        sortable: true,
                        render: ({checked_in}) => (
                            <BooleanStatusIcon
                                value={Boolean(checked_in)}
                                trueLabel="Checked in"
                                falseLabel="Not checked in"
                            />
                        ),
                    },
                    {
                        accessor: 'checked_in_at',
                        title: 'Check-In Date',
                        sortable: true,
                        render: ({checked_in_at}) => formatAdminDate(checked_in_at),
                    },
                    {
                        accessor: 'check-out',
                        title: '',
                        render: (registration) => (
                            registration.checked_in ? (
                                <CheckOutAction onClick={() => handleCheckOut(registration)}/>
                            ) : null
                        ),
                        filter: (
                            <Stack gap="xs">
                                <Checkbox
                                    label="Hide checked-in"
                                    checked={hideCheckedIn}
                                    onChange={(event) => setHideCheckedIn(event.currentTarget.checked)}
                                />
                                <Checkbox
                                    label="Hide unpaid"
                                    checked={hideUnpaid}
                                    onChange={(event) => setHideUnpaid(event.currentTarget.checked)}
                                />
                            </Stack>
                        ),
                    },
                ]}
                rowStyle={(record) => {
                    if (isStaffRegistration(record, staffEmails)) {
                        return {backgroundColor: rgba(theme.colors.violet[8], 0.2)};
                    }
                    return undefined;
                }}
            />
        </Stack>
    );
};

const registrationLabel = (registration: Registration) =>
    `${registration.lastname}, ${registration.firstname} (#${registration.id})`;

type CheckInDetailsTableProps = {
    age: number;
    paid: string;
    tshirt: string;
    isStaff: boolean;
};

function CheckInDetailsTable({age, paid, tshirt, isStaff}: CheckInDetailsTableProps) {
    return (
        <Table withTableBorder striped highlightOnHover>
            <Table.Tbody>
                <Table.Tr>
                    <Table.Td fw={500} w="40%">Age</Table.Td>
                    <Table.Td>{age}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                    <Table.Td fw={500}>Paid</Table.Td>
                    <Table.Td>
                        {isStaff ? (
                            <Badge color="violet" variant="light">Staff</Badge>
                        ) : (
                            <PaidStatusIcon paid={paid}/>
                        )}
                    </Table.Td>
                </Table.Tr>
                <Table.Tr>
                    <Table.Td fw={500}>
                        <ThemeIcon color="orange" variant="light" size="sm" radius="xl" aria-label="T-shirt size">
                            <IconShirt size={14} stroke={1.5}/>
                        </ThemeIcon>
                    </Table.Td>
                    <Table.Td>{tshirt}</Table.Td>
                </Table.Tr>
            </Table.Tbody>
        </Table>
    );
}

const QrReader = () => {
    const queryClient = useQueryClient();
    const staffEmails = useStaffEmails();
    const {data: registrations} = useQuery({
        queryKey: ['registrations'],
        queryFn: RegistrationService.getAllRegistrationsApiV1RegistrationsGet,
    });

    const registrationOptions = useMemo((): ComboboxItem[] => {
        return (registrations ?? [])
            .filter((registration) => !registration.checked_in)
            .map((registration) => ({
                value: String(registration.id),
                label: registrationLabel(registration),
            }));
    }, [registrations]);
    const scanner = useRef<QrScanner>(null);
    const videoEl = useRef<HTMLVideoElement>(null);
    const qrBoxEl = useRef<HTMLDivElement>(null);
    const [qrOn, setQrOn] = useState<boolean>(true);

    const [scannedData, setScannedData] = useState<string | null>(null);
    const alreadyScanned = new Set<string>();

    const [isCheckInModalOpen, setCheckInModalOpen] = useState(false);
    const [checkInStatus, setCheckInStatus] = useState<CheckInModalState>(emptyCheckInModalState);
    const [manualInput, setManualInput] = useState('');
    const [debouncedManualInput] = useDebouncedValue(manualInput, 500);

    const scanned = (code: string) => {
        const decodedText = code;
        if (!decodedText || alreadyScanned.has(decodedText)) {
            return;
        }
        const registrationId = toNumber(decodedText);
        if (!Number.isFinite(registrationId)) {
            return;
        }
        alreadyScanned.add(decodedText);
        setTimeout(() => alreadyScanned.delete(decodedText), 3000);

        try {
            RegistrationService.checkInApiV1RegistrationCheckInRegistrationIdGet({
                registrationId
            }).then((registration) => {
                const modalState = buildCheckInModalState(registration, staffEmails);
                setScannedData(decodedText);
                setCheckInStatus(modalState);
                if (modalState.fullName !== '') {
                    setCheckInModalOpen(true);
                }
            }).catch(() => {});
        } catch {
            alert('Check-in failed');
        }
    };

    const handleConfirmCheckIn = async () => {
        if (scannedData) {
            try {
                RegistrationService.confirmCheckInApiV1RegistrationCheckInRegistrationIdPut({
                    registrationId: toNumber(scannedData)
                }).then(() => {
                    setCheckInModalOpen(false);
                    queryClient.invalidateQueries({queryKey: ['registrations']});
                });
            } catch {
                alert('Check-in failed');
            }
        }
    };

    const onScanSuccess = (result: QrScanner.ScanResult) => {
        scanned(result.data);
    };

    useEffect(() => {
        const videoElement = videoEl.current;
        if (videoElement && !scanner.current) {
            scanner.current = new QrScanner(videoElement, onScanSuccess, {
                preferredCamera: "environment",
                highlightScanRegion: true,
                highlightCodeOutline: true,
                overlay: qrBoxEl?.current || undefined,
            });

            scanner?.current
                ?.start()
                .then(() => setQrOn(true))
                .catch((err) => {
                    if (err) setQrOn(false);
                });
        }

        return () => {
            if (!videoElement) {
                scanner?.current?.pause();
            }
        };
    }, []);

    useEffect(() => {
        if (!qrOn) {
            alert(
                "Camera is blocked or not accessible. Please allow camera in your browser permissions and Reload."
            );
        }
    }, [qrOn]);

    useEffect(() => {
        const trimmed = debouncedManualInput.trim();
        if (/^\d+$/.test(trimmed)) {
            onScanSuccess({
                data: trimmed,
                cornerPoints: [],
            });
        }
    }, [debouncedManualInput]);

    const handleManualOptionSubmit = (registrationId: string) => {
        setManualInput(registrationId);
        scanned(registrationId);
    };

    return (
        <>
            <Paper withBorder radius="md" p="md" className="qr-reader" h="60%">
                <video ref={videoEl} />
            </Paper>
            <Modal
                opened={isCheckInModalOpen}
                centered
                onClose={() => setCheckInModalOpen(false)}
                title={(
                    <Group gap="sm">
                        <Text>{checkInStatus.fullName}</Text>
                        {checkInStatus.isStaff ? (
                            <Badge color="violet" variant="light">Staff</Badge>
                        ) : null}
                    </Group>
                )}
                transitionProps={{ transition: 'rotate-left' }}
            >
                <Stack gap="md">
                    <CheckInDetailsTable
                        age={checkInStatus.age}
                        paid={checkInStatus.paid}
                        tshirt={checkInStatus.tshirt}
                        isStaff={checkInStatus.isStaff}
                    />
                    {checkInStatus.requiresParentalConsent ? (
                        <Alert color="yellow" variant="light" title="Warning">
                            Parental consent required!
                        </Alert>
                    ) : null}
                    {checkInStatus.isUnpaid ? (
                        <Alert color="yellow" variant="light" title="Warning">
                            Registration is not marked as paid.
                        </Alert>
                    ) : null}
                    {checkInStatus.alreadyCheckedIn ? (
                        <Alert color="red" variant="light" title="Error">
                            Already checked in
                        </Alert>
                    ) : null}
                    <Group justify="space-between">
                        <Button variant="default" onClick={() => setCheckInModalOpen(false)}>Close</Button>
                        <Button
                            disabled={checkInStatus.alreadyCheckedIn}
                            onClick={handleConfirmCheckIn}
                        >
                            {confirmCheckInLabel(checkInStatus.isUnpaid)}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
            <Paper withBorder radius="md" p="md">
                <Stack gap="xs">
                    <Text fw={500}>Manual check-in</Text>
                    <Text size="sm" c="dimmed">
                        Use this if the scanner does not work.
                    </Text>
                    <Autocomplete
                        placeholder="Registration ID or name"
                        value={manualInput}
                        onChange={setManualInput}
                        onOptionSubmit={handleManualOptionSubmit}
                        data={registrationOptions}
                        limit={15}
                        filter={({options, search, limit}) => {
                            const query = search.trim().toLowerCase();
                            if (!query) {
                                return options.slice(0, limit);
                            }
                            const filtered = options.filter((option) => {
                                if (!('value' in option)) {
                                    return false;
                                }
                                const label = option.label ?? option.value;
                                return (
                                    option.value.includes(query)
                                    || label.toLowerCase().includes(query)
                                );
                            });
                            return filtered.slice(0, limit);
                        }}
                    />
                    <Text size="xs" c="dimmed">
                        Enter an ID (checked automatically) or search by name and pick a suggestion
                    </Text>
                </Stack>
            </Paper>
        </>
    );
};

const CheckInComponent: React.FC = () => {
    return (
        <Stack gap="md">
            <Text size="sm" c="dimmed">Scan the QR code below to check in:</Text>
            <QrReader/>
        </Stack>
    );
};

export const CheckIn = () => {
    return (
        <PageLayout title="Check-In" description="Scan QR codes or search registrations to check in participants.">
            <CheckInComponent/>
            <CheckInOverview/>
        </PageLayout>
    );
};
